import { PCMAudioManager } from '@/lib/pcmAudioManager';
import { computeInt16Rms } from '@/lib/silenceDetection';
import type { StreamingClient } from '@/lib/streamingClient';
import { useEffect, useRef, useState } from 'react';
import { type InterpreterRow, ackSchema, recordRequest } from './api';
import type { InterpreterOptions, Phase } from './interpreter.types';
import { connectInterpreterSocket } from './interpreterSocket';
import { useInterpreterAudio } from './useInterpreterAudio';
import { useInterpreterPlayback } from './useInterpreterPlayback';
import { useInterpreterRecord } from './useInterpreterRecord';
export type { InterpreterOptions, InterpreterSummaryOptions, InterpreterSummaryResult } from './interpreter.types';

export const useInterpreter = (id: string | undefined, options: InterpreterOptions = {}) => {
  const mounted = useRef(true);
  const { recordingAudio, audioError, audioRejected, audioSaving, audioUrl, saveAudio, restoreAudio, prepareAudio } =
    useInterpreterAudio(id, mounted);
  const [phase, setPhase] = useState<Phase>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [handoffPending, setHandoffPending] = useState(false);
  const [partial, setPartial] = useState('');
  const [message, setMessage] = useState('');
  const active = useRef(false);
  const { query, refresh, store, summary, clearSummary, summarize } = useInterpreterRecord(id, {
    active,
    setPhase,
    setMessage,
  });
  const capturing = useRef(false);
  const currentTurn = useRef<string | null>(null);
  const client = useRef<StreamingClient | null>(null);
  const manager = useRef<PCMAudioManager | null>(null);
  const pending = useRef(Promise.resolve());
  const captureAbort = useRef<AbortController | null>(null);
  const failed = useRef(false);
  const speakTurn = useRef(true);
  const lastStopAcknowledged = useRef(false);
  const restored = useRef(false);
  const { play, cancel: cancelSpeech } = useInterpreterPlayback({ captureAbort, mounted, failed, setPhase });

  useEffect(() => {
    if (!recordingAudio.id) return;
    restored.current = false;
  }, [recordingAudio]);

  useEffect(() => {
    if (!query.data || restored.current) return;
    restored.current = true;
    if (query.data.source_audio_saved) restoreAudio();
    const rows = query.data.utterances.filter((row) => row.turn_id === query.data?.config.turn_id);
    setHandoffPending(query.data.config.phase === 'playback' || rows.some((row) => row.translation_status !== 'ready'));
  }, [query.data, restoreAudio]);

  useEffect(() => {
    if (query.data !== null) return;
    captureAbort.current?.abort();
    active.current = false;
    capturing.current = false;
    void manager.current?.stop();
    client.current?.disconnect();
    cancelSpeech();
    setPartial('');
    clearSummary();
    setAudioLevel(0);
    setHandoffPending(false);
    setPhase('idle');
  }, [query.data, cancelSpeech, clearSummary]);

  const fail = (run = captureAbort.current) => {
    if (run !== captureAbort.current) return;
    run?.abort();
    failed.current = true;
    active.current = false;
    capturing.current = false;
    manager.current?.pause();
    recordingAudio.capture(manager.current);
    void manager.current?.stop();
    void saveAudio();
    client.current?.disconnect();
    cancelSpeech();
    if (mounted.current) {
      setPhase('idle');
      setAudioLevel(0);
      setPartial('');
      setMessage('Upptaka stöðvaðist. Skoðaðu vistaðan texta áður en þú heldur áfram.');
      refresh();
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      captureAbort.current?.abort();
      failed.current = true;
      active.current = false;
      capturing.current = false;
      manager.current?.pause();
      recordingAudio.capture(manager.current);
      void manager.current?.stop();
      void recordingAudio.flush().catch(() => undefined);
      client.current?.disconnect();
      cancelSpeech();
    };
  }, [recordingAudio, cancelSpeech]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (capturing.current || recordingAudio.unsaved) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [recordingAudio]);

  const start = async (speaker: 'doctor' | 'patient') => {
    if (!id || active.current || handoffPending) return;
    active.current = true;
    capturing.current = true;
    const run = new AbortController();
    captureAbort.current?.abort();
    captureAbort.current = run;
    failed.current = false;
    speakTurn.current = speaker === 'doctor' ? options.speakPatient !== false : options.speakClinician !== false;
    lastStopAcknowledged.current = false;
    setElapsed(0);
    setPhase('starting');
    setMessage('');
    clearSummary();
    try {
      await prepareAudio();
      if (options.deviceId && navigator.mediaDevices?.enumerateDevices) {
        const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
          (device) => device.kind === 'audioinput'
        );
        if (
          devices.some((device) => device.deviceId) &&
          !devices.some((device) => device.deviceId === options.deviceId)
        ) {
          throw new DOMException('microphone_unavailable', 'NotFoundError');
        }
      }
      await connectInterpreterSocket({
        id,
        client,
        currentTurn,
        mounted,
        onChanged: refresh,
        onPartial: setPartial,
        onDisconnect: () => {
          if (capturing.current) fail();
        },
      });
      const socket = client.current?.getSocket();
      if (!socket || !mounted.current || run.signal.aborted) throw new Error('connection_failed');
      const result = ackSchema.parse(await socket.timeout(20000).emitWithAck('interpreter_start', { id, speaker }));
      if (!result.ok || !result.record || run.signal.aborted || !mounted.current) throw new Error('start_failed');
      store(result.record);
      const turnId = result.record.config.turn_id;
      currentTurn.current = turnId;
      const capture = new PCMAudioManager({
        sampleRate: 16000,
        retainAudio: recordingAudio.enabled,
        transmitSilence: true,
        transmitInterval: 250,
      });
      manager.current = capture;
      pending.current = Promise.resolve();
      let queuedChunks = 0;
      await capture.start(options.deviceId || undefined, (chunk) => {
        if (run.signal.aborted || !active.current) return;
        setAudioLevel(Math.min(1, computeInt16Rms(chunk.pcmData ?? new Int16Array(chunk.pcmArrayBuffer)) * 6));
        if (Number.isFinite(chunk.totalDuration)) setElapsed(chunk.totalDuration);
        if (++queuedChunks > 16) {
          queuedChunks--;
          fail(run);
          return;
        }
        pending.current = pending.current
          .then(async () => {
            if (run.signal.aborted || !mounted.current) return;
            const ack = ackSchema.parse(
              await socket.timeout(10000).emitWithAck('interpreter_pcm', chunk.pcmArrayBuffer, { id, turn_id: turnId })
            );
            if (!ack.ok) throw new Error('stream_failed');
          })
          .catch(() => {
            if (!run.signal.aborted) fail(run);
          })
          .finally(() => {
            queuedChunks--;
          });
      });
      if (!mounted.current || run.signal.aborted) {
        await capture.stop();
        return;
      }
      setPhase('speaking');
    } catch (error) {
      if (!run.signal.aborted) {
        fail(run);
        if (
          error instanceof DOMException &&
          ['NotFoundError', 'OverconstrainedError', 'NotAllowedError'].includes(error.name)
        ) {
          setMessage(
            'Ekki tókst að tengjast völdum hljóðnema. Opnaðu stillingar samtals, sæktu hljóðnema og veldu tiltækan hljóðnema.'
          );
        }
      }
    }
  };

  const stop = async (withSpeech = true): Promise<boolean> => {
    if (!id || !['speaking', 'paused'].includes(phase)) return false;
    const run = captureAbort.current;
    lastStopAcknowledged.current = false;
    setPhase('finishing');
    setAudioLevel(0);
    try {
      manager.current?.pause();
      recordingAudio.capture(manager.current);
      await manager.current?.stop();
      await saveAudio();
      await pending.current;
      if (run?.signal.aborted || run !== captureAbort.current) return false;
      const socket = client.current?.getSocket();
      if (!socket) throw new Error('connection_failed');
      const result = ackSchema.parse(
        await socket.timeout(150000).emitWithAck('interpreter_stop', { id, turn_id: currentTurn.current })
      );
      if (run?.signal.aborted || run !== captureAbort.current) return false;
      if (!result.ok || !result.record) throw new Error('stop_failed');
      lastStopAcknowledged.current = true;
      capturing.current = false;
      setPartial('');
      store(result.record);
      setHandoffPending(result.record.utterances.some((row) => row.turn_id === result.record?.config.turn_id));
      if (result.code) {
        setMessage(
          result.code === 'no_speech'
            ? 'Ekkert tal greindist. Athugaðu hljóðnemann og reyndu aftur.'
            : result.code === 'source_limit'
              ? 'Viðtalið hefur náð textamörkum. Vistaður texti er aðgengilegur.'
              : 'Ekki tókst að ljúka allri vinnslu. Skoðaðu textann og reyndu óloknar þýðingar aftur.'
        );
        setPhase('idle');
      } else {
        if (withSpeech && speakTurn.current) await play(result.record);
        if (!mounted.current || run?.signal.aborted || run !== captureAbort.current) return false;
        store(await recordRequest('POST', `/${id}/pause`, {}));
        if (mounted.current && run === captureAbort.current) setPhase('idle');
        setElapsed(0);
        setHandoffPending(false);
        return true;
      }
    } catch {
      if (run?.signal.aborted || run !== captureAbort.current || !mounted.current) return false;
      setPhase('idle');
      setMessage('Ekki tókst að ljúka vinnslu eða spila hljóð. Vistaður texti er aðgengilegur. Þú getur prófað aftur.');
      refresh();
    } finally {
      if (run === captureAbort.current) {
        active.current = false;
        capturing.current = false;
      }
    }
    return false;
  };

  const pauseCapture = async () => {
    if (phase !== 'speaking' || !active.current) return;
    const run = captureAbort.current;
    setPhase('pausing');
    manager.current?.pause();
    recordingAudio.capture(manager.current);
    await saveAudio();
    await pending.current;
    if (!mounted.current || run?.signal.aborted || run !== captureAbort.current) return;
    setAudioLevel(0);
    setPhase('paused');
  };

  const resumeCapture = () => {
    if (phase !== 'paused' || !active.current || captureAbort.current?.signal.aborted) return;
    manager.current?.resume();
    setPhase('speaking');
  };

  const end = async () => {
    if (!id) return;
    let preserveWarning = false;
    if (['speaking', 'paused'].includes(phase)) {
      const completed = await stop(false);
      if (!completed && !lastStopAcknowledged.current) return;
      preserveWarning = !completed;
    } else if (active.current) return;
    if (recordingAudio.unsaved && !(await saveAudio())) return;
    active.current = true;
    setPhase('finishing');
    if (!preserveWarning) setMessage('');
    try {
      store(await recordRequest('POST', `/${id}/end`, {}));
    } catch {
      setMessage('Ekki tókst að staðfesta lok viðtals. Vistaður texti er aðgengilegur. Reyndu aftur.');
      refresh();
    } finally {
      active.current = false;
      setPhase('idle');
    }
  };

  const retry = async (row: InterpreterRow) => {
    if (!id || active.current) return;
    active.current = true;
    setPhase('finishing');
    setMessage('');
    try {
      await recordRequest('POST', `/${id}/pause`, {});
      store(await recordRequest('POST', `/${id}/retry/${row.turn_id}/${row.n}`, {}));
    } catch {
      setMessage('Ekki tókst að þýða. Upprunalegi textinn er enn vistaður.');
    } finally {
      active.current = false;
      setPhase('idle');
    }
  };

  const replay = async (): Promise<boolean> => {
    if (!query.data || active.current) return false;
    active.current = true;
    const run = new AbortController();
    captureAbort.current = run;
    failed.current = false;
    setPhase('playing');
    try {
      const record = await recordRequest('GET', `/${id}`);
      store(record);
      await play(record);
      if (!mounted.current || run.signal.aborted || run !== captureAbort.current) return false;
      store(await recordRequest('POST', `/${id}/pause`, {}));
      setHandoffPending(false);
      return true;
    } catch {
      setMessage('Ekki tókst að spila þýðinguna. Textinn er aðgengilegur hér fyrir neðan.');
    } finally {
      active.current = false;
      setPhase('idle');
    }
    return false;
  };

  const continueWithoutSpeech = async (): Promise<boolean> => {
    const record = query.data;
    if (!record || active.current || !handoffPending) return false;
    const rows = record.utterances.filter((row) => row.turn_id === record.config.turn_id);
    if (!rows.length || rows.some((row) => row.translation_status !== 'ready')) return false;
    active.current = true;
    setPhase('finishing');
    setMessage('');
    try {
      store(await recordRequest('POST', `/${id}/pause`, {}));
      setHandoffPending(false);
      setElapsed(0);
      return true;
    } catch {
      setMessage('Ekki tókst að staðfesta lok lotunnar. Reyndu aftur áður en næsti ræðumaður talar.');
      return false;
    } finally {
      active.current = false;
      setPhase('idle');
    }
  };

  return {
    record: query.data ?? undefined,
    audioError,
    audioRejected,
    audioSaving,
    audioUrl,
    retryAudio: saveAudio,
    isLoading: query.isLoading,
    isError: query.isError || (Boolean(id) && query.data === null),
    phase,
    audioLevel,
    elapsed,
    handoffPending,
    continueWithoutSpeech,
    pauseCapture,
    resumeCapture,
    end,
    partial,
    message,
    summary,
    start,
    stop,
    retry,
    replay,
    summarize,
  };
};
